/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { Capacitor } from "@capacitor/core";
import { Contacts } from "@capacitor-community/contacts";
import type { ContactsPlugin } from "@capacitor-community/contacts";

export type ContactPickerResult =
  | { status: "selected"; name: string; phone: string }
  | { status: "multi-number"; name: string; numbers: string[] }
  | { status: "no-phone" }
  | { status: "denied" }
  | { status: "cancelled" }
  | { status: "unsupported" }
  | { status: "error" };

/**
 * Opens the native contacts picker and normalizes the result. On non-iOS
 * platforms it returns `unsupported` so callers can fall back to manual entry.
 * Kept pure (plugin injected) so callers can test the state machine.
 */
export async function pickEmergencyContact(
  plugin: Pick<ContactsPlugin, "checkPermissions" | "requestPermissions" | "pickContact"> = Contacts,
  platform: string = Capacitor.getPlatform(),
): Promise<ContactPickerResult> {
  if (platform !== "ios") {
    return { status: "unsupported" };
  }

  try {
    const current = await plugin.checkPermissions();
    if (current.contacts !== "granted" && current.contacts !== "limited") {
      const requested = await plugin.requestPermissions();
      if (requested.contacts !== "granted" && requested.contacts !== "limited") {
        return { status: "denied" };
      }
    }

    const { contact } = await plugin.pickContact({
      projection: { name: true, phones: true },
    });

    const displayName =
      contact?.name?.display?.trim() ||
      [contact?.name?.given, contact?.name?.family].filter(Boolean).join(" ").trim() ||
      "";

    const numbers = (contact?.phones ?? [])
      .map((p) => (p?.number ?? "").trim())
      .filter((n) => n.length > 0);

    if (numbers.length === 0) {
      return { status: "no-phone" };
    }
    if (numbers.length === 1) {
      return { status: "selected", name: displayName, phone: numbers[0] };
    }
    return { status: "multi-number", name: displayName, numbers };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // The iOS picker rejects with a "cancelled" style message when the user
    // dismisses the sheet — treat that as a non-error cancel.
    if (/cancel/i.test(message)) {
      return { status: "cancelled" };
    }
    return { status: "error" };
  }
}
